import { cn } from '@/lib/cn';

export type Segment<T extends string> = {
  value: T;
  label: string;
};

/** Compact single-select control for view switching and range pickers. */
export function SegmentedControl<T extends string>({
  segments,
  value,
  onChange,
  size = 'md',
  className,
}: {
  segments: Segment<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={cn(
        'inline-flex max-w-full gap-0.5 overflow-x-auto rounded-xl bg-elevated p-1 ring-1 ring-inset ring-line scroll-thin',
        className
      )}
    >
      {segments.map((segment) => {
        const selected = segment.value === value;
        return (
          <button
            key={segment.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(segment.value)}
            className={cn(
              'shrink-0 whitespace-nowrap rounded-[9px] font-semibold transition',
              size === 'sm' ? 'h-7 px-2.5 text-[12px]' : 'h-8 px-3.5 text-[13px]',
              selected
                ? 'bg-surface text-fg shadow-[0_1px_3px_rgb(0_0_0/0.1)] dark:bg-primary/20 dark:text-primary'
                : 'text-muted hover:text-fg'
            )}
          >
            {segment.label}
          </button>
        );
      })}
    </div>
  );
}

/** Multi-select pill, used by the expense filter panel. */
export function FilterChip({
  label,
  selected,
  onClick,
  color,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  color?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[12px] font-medium transition',
        selected
          ? 'border-primary/40 bg-primary/10 text-primary'
          : 'border-line bg-surface text-muted hover:text-fg'
      )}
    >
      {color && (
        <span className="size-2 rounded-full" style={{ backgroundColor: color }} aria-hidden />
      )}
      {label}
    </button>
  );
}
